import { useEffect, useId, useState } from 'react';
import { Search, User, Mail, Calendar } from 'lucide-react';
import { agentApi } from '@/services/agentApi';

export type SavedStudentProfile = Record<string, unknown> & {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  dateOfBirth?: string | null;
};

export function SavedProfilePicker({ onSelect, disabled }: {
  onSelect: (profile: SavedStudentProfile) => void | boolean;
  disabled?: boolean;
}) {
  const toggleId = useId();
  const [useProfile, setUseProfile] = useState(false);
  const [profiles, setProfiles] = useState<SavedStudentProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  useEffect(() => {
    if (!useProfile) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    const query = searchTerm.trim();
    const timer = setTimeout(() => {
      void agentApi.getStudentProfiles({ limit: 100, search: query || undefined }, controller.signal)
        .then(({ profiles }) => {
          if (!controller.signal.aborted) setProfiles(profiles || []);
        })
        .catch(() => {
          if (!controller.signal.aborted) setLoadError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, query ? 300 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [useProfile, searchTerm, retry]);

  if (!useProfile) {
    return (
      <div>
        <h5 className="fw-bold mb-3">Load from Saved Profile</h5>
        <p className="text-muted mb-3">
          Would you like to start this application with data from a saved student profile?
        </p>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={toggleId}
            checked={useProfile}
            disabled={disabled}
            onChange={(e) => setUseProfile(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={toggleId}>
            Yes, load from a saved profile
          </label>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h5 className="fw-bold mb-3">Select Saved Profile</h5>
      <p className="text-muted mb-3">
        Select a saved student profile to pre-fill this application form.
      </p>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id={toggleId}
          checked={useProfile}
          disabled={disabled}
          onChange={(e) => {
            setUseProfile(e.target.checked);
            if (!e.target.checked) {
              setSelectedProfileId(null);
            }
          }}
        />
        <label className="form-check-label" htmlFor={toggleId}>
          Load from saved profile
        </label>
      </div>

      {useProfile && (
        <>
          {/* Search */}
          <div className="mb-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm"
                aria-label="Search saved student profiles"
                placeholder="Search profiles by name or email..."
                value={searchTerm}
                disabled={disabled}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              <Search size={16} className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted" />
            </div>
          </div>

          {/* Profile List */}
          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading profiles...</span>
              </div>
            </div>
          ) : loadError ? (
            <div className="alert alert-warning" role="alert">
              Could not load saved profiles. <button type="button" className="btn btn-link p-0" disabled={disabled} onClick={() => setRetry((value) => value + 1)}>Retry</button>
            </div>
          ) : profiles.length === 0 ? (
            <div className="alert alert-info">
              <strong>No profiles found.</strong>
              <p className="mb-0">You can save profiles after submitting applications.</p>
            </div>
          ) : (
            <div className="list-group">
              {profiles.map(profile => (
                <button
                  key={profile.id}
                  type="button"
                  className={`list-group-item list-group-item-action ${selectedProfileId === profile.id ? 'active' : ''}`}
                  disabled={disabled}
                  onClick={() => {
                    if (onSelect(profile) !== false) setSelectedProfileId(profile.id);
                  }}
                >
                  <div className="d-flex w-100 justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">
                        <User size={16} className="me-2" />
                        {profile.firstName} {profile.middleName} {profile.lastName}
                      </h6>
                      <div className="small text-muted">
                        <Mail size={12} className="me-1" />
                        {profile.email}
                      </div>
                      {profile.dateOfBirth && (
                        <div className="small text-muted">
                          <Calendar size={12} className="me-1" />
                          {new Date(profile.dateOfBirth).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {selectedProfileId === profile.id && (
                      <span className="badge bg-success">Selected</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
